// src/api/endpoints.js

// API Endpoints (relative to baseURL, which should end with /api/)
export const endpoints = {
  // Authentication
  AUTH_REGISTER: "auth/register/",
  AUTH_LOGIN: "auth/login/",
  AUTH_REFRESH: "auth/token/refresh/",
  AUTH_ME: "auth/me/",
  AUTH_CHANGE_PASSWORD: "auth/change-password/",
  AUTH_PASSWORD_RESET_REQUEST: "auth/password-reset/request/",
  AUTH_PASSWORD_RESET_VERIFY: "auth/password-reset/verify/",
  AUTH_PASSWORD_RESET_CONFIRM: "auth/password-reset/confirm/",

  // Users
  GET_ALL_USERS: "users/",
  UPDATE_USER: (id) => `users/${id}/`,

  // Reports
  CREATE_REPORT: (clinic) => `reports/clinic/${clinic}/`,
  UPDATE_REPORT: (clinic, id) => `reports/clinic/${clinic}/${id}/`,
  GET_REPORT_CLINIC_DATA: (clinic) => `reports/clinic/${clinic}/`,
  GET_REPORT_TOTAL: "reports/total/",
  GET_REPORT_TOTAL_MONTH_PRACTITIONERS: (clinic) => `reports/clinic/${clinic}/total-month/`,

  // Practitioners
  GET_ALL_PRACTITIONERS: "practitioners/",
  UPDATE_PRACTITIONER: (id) => `practitioners/${id}/`,
  UPLOAD_PRACTITIONERS: "practitioners/upload/",

  // Patients
  GET_ALL_PATIENTS: "patients/",
  CREATE_PATIENT: "patients/",
  UPDATE_PATIENT: (id) => `patients/${id}/`,
  DELETE_PATIENT: (id) => `patients/${id}/`,

  // Forecasts
  GET_ALL_FORECASTS: "forecasts/",
  CREATE_FORECAST: "forecasts/",
  UPDATE_FORECAST: (id) => `forecasts/${id}/`,
  GET_FORECAST_MTD: "forecasts/mtd/",

  // Clinics
  GET_ALL_CLINICS: "clinics/",
  UPDATE_CLINIC: (id) => `clinics/${id}/`,
  GET_CLINIC_SUBSIDIARY_MAP: "clinics/subsidiary-map/",
  GET_CLINIC_BANK_ACCOUNTS: "clinics/bank-accounts/",

  // Practitioner Profiles
  VERIFY_PRACTITIONER_NAME: "practitioner-profiles/verify/",

  // Collection Breakdown
  GET_CLINIC_COLLECTION_BREAKDOWNS: (clinic) => `collection-breakdown/clinic/${clinic}/`,
  CREATE_COLLECTION_BREAKDOWN: (clinic) => `collection-breakdown/clinic/${clinic}/`,
  UPDATE_COLLECTION_BREAKDOWN: (clinic, id) => `collection-breakdown/clinic/${clinic}/${id}/`,
  GET_COLLECTION_BREAKDOWN_CLINICS: "collection-breakdown/clinics/",
  GET_MULTI_CLINIC_COLLECTION_BREAKDOWNS: "collection-breakdown/multi-clinics/",

  // Overview
  GET_OVERVIEW_CLINIC_SUMMARY: "overview/clinic/",
  GET_OVERVIEW_PERFORMANCE: "overview/clinic/performance/",
  GET_OVERVIEW_PATIENTS: "overview/clinic/patients/",
  GET_OVERVIEW_FORECAST_TOTAL: "overview/forecast/total/",

  // Shared (admin + clinic use same paths)
  GET_REPORTS_SUMMARY: "reports/summary/",
  GET_PRACTITIONER_PRODUCTION: "reports/practitioner-production/",
  GET_REPORTS_COLLECTION_SUMMARY: "reports/collection-summary/",
  GET_CLINIC_DATA: (clinic) => `reports/clinic-data/${clinic}/`,
  GET_ASSOCIATE_FEE_ROWS: "monthly-compensation/associate-fee/",

  // Practitioner Vacations
  GET_PRACTITIONER_VACATIONS: "practitioner-vacations/",
  CREATE_PRACTITIONER_VACATION: "practitioner-vacations/",
  UPDATE_PRACTITIONER_VACATION: (id) => `practitioner-vacations/${id}/`,
  DELETE_PRACTITIONER_VACATION: (id) => `practitioner-vacations/${id}/`,

  // Clinic Vacations
  GET_CLINIC_VACATIONS: "clinic-vacations/",
  CREATE_CLINIC_VACATION: "clinic-vacations/",
  UPDATE_CLINIC_VACATION: (id) => `clinic-vacations/${id}/`,
  DELETE_CLINIC_VACATION: (id) => `clinic-vacations/${id}/`,

  // Copy Schedule
  COPY_SCHEDULE: "practitioners/copy-schedule/",

  // Practitioner Weekly Schedules — practitioner_weekly_schedule table.
  // A row with effective_start/effective_end overrides the practitioner's base row
  // (both null) for dates inside that range. Pass ?date=YYYY-MM-DD to have the server
  // return only the row in effect per practitioner; omit it to get every row and
  // resolve client-side with @utils/weeklySchedule.
  GET_PRACTITIONER_WEEKLY_SCHEDULES: "practitioner-weekly-schedules/",
  CREATE_PRACTITIONER_WEEKLY_SCHEDULE: "practitioner-weekly-schedules/",
  UPDATE_PRACTITIONER_WEEKLY_SCHEDULE: (id) => `practitioner-weekly-schedules/${id}/`,
  DELETE_PRACTITIONER_WEEKLY_SCHEDULE: (id) => `practitioner-weekly-schedules/${id}/`,

  // Practitioner Schedule Overrides
  GET_PRACTITIONER_SCHEDULE_OVERRIDES: "practitioner-schedule-overrides/",
  CREATE_PRACTITIONER_SCHEDULE_OVERRIDE: "practitioner-schedule-overrides/",
  UPDATE_PRACTITIONER_SCHEDULE_OVERRIDE: (id) => `practitioner-schedule-overrides/${id}/`,
  DELETE_PRACTITIONER_SCHEDULE_OVERRIDE: (id) => `practitioner-schedule-overrides/${id}/`,

  // Clinic Schedule Overrides
  GET_CLINIC_SCHEDULE_OVERRIDES: "clinic-schedule-overrides/",
  CREATE_CLINIC_SCHEDULE_OVERRIDE: "clinic-schedule-overrides/",
  UPDATE_CLINIC_SCHEDULE_OVERRIDE: (id) => `clinic-schedule-overrides/${id}/`,
  DELETE_CLINIC_SCHEDULE_OVERRIDE: (id) => `clinic-schedule-overrides/${id}/`,

  // Audit Logs
  GET_AUDIT_LOGS: "audit-logs/",

  // NetSuite
  CREATE_NETSUITE_JOURNAL_ENTRY: "netsuite/journal-entries/",
  SEARCH_NETSUITE_JOURNAL_ENTRIES: "netsuite/journal-entries/search/",
  SEARCH_NETSUITE_JOURNAL_ENTRIES_BY_MONTH_YEAR: "netsuite/journal-entries/search-by-month-year/",
  CREATE_NETSUITE_ADVANCED_INTERCOMPANY_JOURNAL_ENTRY:
    "netsuite/advanced-intercompany-journal-entries/",
  SEARCH_NETSUITE_ADVANCED_INTERCOMPANY_JOURNAL_ENTRIES:
    "netsuite/advanced-intercompany-journal-entries/search/",
  SEARCH_NETSUITE_CASH_MANAGEMENT_JOURNAL_ENTRIES:
    "netsuite/cash-management-journal-entries/search/",
  // Normal (non-intercompany) journal entries by memo — the single-subsidiary
  // entries that the advanced-intercompany search cannot see.
  SEARCH_NETSUITE_CASH_MANAGEMENT_JE:
    "netsuite/cash-management-journal-entries/cash-management-je/",
  CREATE_NETSUITE_CHECK: "netsuite/checks/",
  BULK_CREATE_NETSUITE_CHECK: "netsuite/checks/bulk/",
  SEARCH_NETSUITE_CHECKS: "netsuite/checks/search/",
  SEARCH_NETSUITE_CHECKS_BY_SUBSIDIARY: "netsuite/checks/search-by-subsidiary/",
  SEARCH_NETSUITE_CHECK_CLEARED_DATES: "netsuite/checks/cleared-dates/",
  SEARCH_NETSUITE_TRANSACTION_LINES: "netsuite/transaction-lines/search/",
  SEARCH_NETSUITE_DEPOSITS: "netsuite/deposit/",
  GET_NETSUITE_ACCOUNTS: "netsuite/accounts/",
  GET_NETSUITE_BANK_BALANCE_ACCOUNTS: "netsuite/accounts/bank-balance/",
  GET_NETSUITE_BANKING_STATEMENTS: "netsuite/banking-statements/",
  // Running end-of-day GL balance per account across a date range.
  GET_NETSUITE_BANK_BALANCES: "netsuite/banking-statements/balances/",
  // The two sides of a bank reconciliation, both keyed by account + day:
  // what the bank sent (imported daily into BankingTransaction) …
  GET_NETSUITE_BANK_FEED_TRANSACTIONS: "netsuite/banking-statements/bank-feed-transactions/",
  // … and what NetSuite has actually booked against the same account.
  GET_NETSUITE_LEDGER_TRANSACTIONS: "netsuite/banking-statements/ledger-transactions/",
  // Scheduled payments matched against the two sides above — which occurrences
  // actually cleared, and which are still owed. Read-only and computed: a
  // recurring schedule is one row covering many occurrences, so `cleared`
  // cannot be stored on it.
  RECONCILE_SCHEDULE_PAYMENTS: "schedule-payment/reconcile/",
  // The only write path for clearance — accepting a suggested match and marking
  // a payment by hand are the same act, so they are one endpoint.
  CONFIRM_SCHEDULE_CLEARED: "schedule-payment/confirm-cleared/",
  GET_NETSUITE_SUBSIDIARIES: "netsuite/subsidiaries/",
  GET_NETSUITE_ACCOUNTING_PERIODS: "netsuite/accounting-periods/",
  GET_NETSUITE_VENDORS: "netsuite/vendors/",
  // Drops every cached SuiteQL result server-side, so the next NetSuite search
  // goes live. Backs the "Sync" button on the Journal Entry and Check tabs.
  INVALIDATE_NETSUITE_CACHE: "netsuite/cache/invalidate/",

  // Lab invoices
  GET_LAB_INVOICE_ATTACHMENTS: "lab-invoices/attachments/",
  GET_LAB_INVOICE_REVIEW_ATTACHMENTS: "lab-invoices/review/",
  GET_LAB_INVOICE_CLINIC_OVERVIEW: "lab-invoices/clinic-overview/",
  GET_LAB_INVOICE_ASSIGNED_DOWNLOADS: "lab-invoices/assigned-downloads/",
  LAB_INVOICE_DOWNLOAD_ZIP: "lab-invoices/download-zip/",
  UPLOAD_LAB_INVOICE: "lab-invoices/upload/",

  // Daily report file attachments (plain storage)
  REPORT_FILES: "report-files/",
  DELETE_REPORT_FILE: (fileId) => `report-files/${fileId}/`,
  GET_REPORT_FILE_VIEW_URL: (fileId) => `report-files/${fileId}/view-url/`,
  // Invisalign invoice PDFs on a clinic-day (plain storage, own GCS folder)
  INVISALIGN_INVOICES: "invisalign-invoices/",
  GET_INVISALIGN_INVOICE_REVIEW: "invisalign-invoices/review/",
  GET_INVISALIGN_INVOICE_CLINIC_OVERVIEW: "invisalign-invoices/clinic-overview/",
  DELETE_INVISALIGN_INVOICE: (invoiceId) => `invisalign-invoices/${invoiceId}/`,
  GET_INVISALIGN_INVOICE_VIEW_URL: (invoiceId) => `invisalign-invoices/${invoiceId}/view-url/`,
  // Insurance EFT totals read from those files (apps/insurance_eft)
  INSURANCE_EFT_DAY: "insurance-eft/",
  // Every counted insurance EFT / cheque payment for one clinic over a range.
  INSURANCE_EFT_PAYMENTS: "insurance-eft/payments/",
  INSURANCE_EFT_TOTALS: "insurance-eft/totals/",
  RESCAN_INSURANCE_EFT: (fileId) => `insurance-eft/files/${fileId}/rescan/`,
  UPDATE_LAB_INVOICE_ATTACHMENT_ASSIGNMENT: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/assignment/`,
  CLAIM_LAB_INVOICE_ATTACHMENT: (attachmentId) => `lab-invoices/attachments/${attachmentId}/claim/`,
  UPDATE_LAB_INVOICE_ATTACHMENT_PDF_EDITS: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/pdf-edits/`,
  REVIEW_LAB_INVOICE_BREAKDOWN: (attachmentId, breakdownId) =>
    `lab-invoices/attachments/${attachmentId}/breakdowns/${breakdownId}/review/`,
  REQUEST_LAB_INVOICE_ATTACHMENT_REVIEW: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/request-review/`,
  RESCAN_LAB_INVOICE_ATTACHMENT: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/rescan/`,
  RETRY_LAB_INVOICE_ATTACHMENT_FROM_EMAIL: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/retry-from-email/`,
  DISCARD_LAB_INVOICE_ATTACHMENT: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/discard/`,
  GET_LAB_INVOICE_ATTACHMENT_VIEW_URL: (attachmentId) =>
    `lab-invoices/attachments/${attachmentId}/view-url/`,
  GET_LEGACY_LAB_INVOICE_ATTACHMENT_VIEW_URL: (invoiceId, attachmentIndex) =>
    `lab-invoices/${invoiceId}/attachments/${attachmentIndex}/view-url/`,

  // Cash Transaction (single source of truth — replaces cash-balance + tracking-cash)
  GET_ALL_CASH_TRANSACTIONS: "cash-transaction/",
  GET_CASH_TRANSACTION: (id) => `cash-transaction/${id}/`,
  CREATE_CASH_TRANSACTION: "cash-transaction/",
  UPDATE_CASH_TRANSACTION: (id) => `cash-transaction/${id}/`,
  GET_CASH_TRANSACTION_EVENTS: (id) => `cash-transaction/${id}/events/`,
  GET_CASH_TRANSACTION_BALANCE: "cash-transaction/balance/",
  GET_CASH_TRANSACTION_BALANCE_DAILY: "cash-transaction/balance/daily/",
  GET_CASH_TRANSACTION_ELIGIBLE_USERS: "cash-transaction/eligible-users/",
  DEPOSIT_CASH_TO_SAFE: "cash-transaction/deposit-to-safe/",

  // Schedule Payment (recurring clinic payments + one-off exceptions)
  GET_ALL_SCHEDULE_PAYMENTS: "schedule-payment/",
  GET_SCHEDULE_PAYMENT: (id) => `schedule-payment/${id}/`,
  CREATE_SCHEDULE_PAYMENT: "schedule-payment/",
  UPDATE_SCHEDULE_PAYMENT: (id) => `schedule-payment/${id}/`,
  DELETE_SCHEDULE_PAYMENT: (id) => `schedule-payment/${id}/`,
  GET_SCHEDULE_PAYMENT_OCCURRENCES: (id) => `schedule-payment/${id}/occurrences/`,
  CREATE_SCHEDULE_PAYMENT_EXCEPTION: "schedule-payment/exceptions/",
  // Upsert MANY exceptions in one request, keyed on (schedule, original_date).
  BULK_SCHEDULE_PAYMENT_EXCEPTIONS: "schedule-payment/exceptions/bulk/",
  UPDATE_SCHEDULE_PAYMENT_EXCEPTION: (id) => `schedule-payment/exceptions/${id}/`,
  DELETE_SCHEDULE_PAYMENT_EXCEPTION: (id) => `schedule-payment/exceptions/${id}/`,

  // Statement Payment (recurring credit-card statement + its payment plan)
  GET_ALL_STATEMENT_PAYMENTS: "statement-payment/",
  GET_STATEMENT_PAYMENT: (id) => `statement-payment/${id}/`,
  CREATE_STATEMENT_PAYMENT: "statement-payment/",
  UPDATE_STATEMENT_PAYMENT: (id) => `statement-payment/${id}/`,
  DELETE_STATEMENT_PAYMENT: (id) => `statement-payment/${id}/`,

  // Statement Plan — the reusable template a statement is created from. Holds no
  // balance and no dates, only the day-of-month pair, so one plan serves every
  // month. At most one per card.
  GET_ALL_STATEMENT_PLANS: "statement-plan/",
  GET_STATEMENT_PLAN: (id) => `statement-plan/${id}/`,
  CREATE_STATEMENT_PLAN: "statement-plan/",
  UPDATE_STATEMENT_PLAN: (id) => `statement-plan/${id}/`,
  DELETE_STATEMENT_PLAN: (id) => `statement-plan/${id}/`,

  // Corporate entities that own bank accounts (Smiles First Corporation, Dens N Dente, ...)
  GET_ALL_ENTITIES: "entities/",

  // Bank accounts — the account registry Daily Bank Balance is keyed on.
  // Supersedes clinics/bank-accounts for balance entry: an account may belong to
  // an entity instead of a clinic, and may have no NetSuite id at all.
  GET_ALL_BANK_ACCOUNTS: "bank-accounts/",
  CREATE_BANK_ACCOUNT: "bank-accounts/",
  UPDATE_BANK_ACCOUNT: (id) => `bank-accounts/${id}/`,
  // Bank lines from the daily bank sync (Meridian), keyed by bank_accounts.id.
  GET_BANK_DAILY_TRANSACTIONS: "bank-daily-transactions/",
  UPLOAD_BANK_CSV: "bank-daily-transactions/upload/",
  // Manual deposit ↔ bank matches (Daily Deposits tab).
  DEPOSIT_MATCHES: "deposit-matches/",
  DEPOSIT_MATCH: (id) => `deposit-matches/${id}/`,
  DEPOSIT_MATCH_DISMISSED: "deposit-matches/dismissed/",
  DEPOSIT_MATCHES_BULK: "deposit-matches/bulk/",

  // Daily Bank Balance (manual daily snapshot of each BANK ACCOUNT's balance —
  // one row per (bank_account, date), so a clinic with two accounts has two rows)
  GET_ALL_DAILY_BANK_BALANCES: "daily-bank-balance/",
  CREATE_DAILY_BANK_BALANCE: "daily-bank-balance/",
  BULK_CREATE_DAILY_BANK_BALANCE: "daily-bank-balance/bulk/",
  UPDATE_DAILY_BANK_BALANCE: (id) => `daily-bank-balance/${id}/`,

  // Intercompany transfers (fund moves between clinics)
  GET_ALL_TRANSFERS: "transfers/",
  CREATE_TRANSFER: "transfers/",
  UPDATE_TRANSFER: (id) => `transfers/${id}/`,
  DELETE_TRANSFER: (id) => `transfers/${id}/`,
};

export const adminEndpoints = {
  GET_ALL_REPORTS: "reports/",
  CREATE_REPORT: "reports/",
  GET_REPORT: (id) => `reports/${id}/`,
  UPDATE_REPORT: (id) => `reports/${id}/`,
  DELETE_REPORT: (id) => `reports/${id}/`,

  GET_ALL_PRACTITIONERS_BY_CLINIC: (clinicCode) => `practitioners/${clinicCode}/`,
  CREATE_PRACTITIONER: `practitioners/`,
  GET_PRACTITIONER_BY_CLINIC: (clinicCode, id) => `practitioners/${clinicCode}/${id}/`,
  UPDATE_PRACTITIONER_BY_CLINIC: (clinicCode, id) => `practitioners/${clinicCode}/${id}/`,
  DELETE_PRACTITIONER_BY_CLINIC: (clinicCode, id) => `practitioners/${clinicCode}/${id}/`,

  // Monthly Compensation (Admin - Role 1, 2)
  GET_ALL_MONTHLY_COMPENSATIONS: "monthly-compensation/",
  CREATE_MONTHLY_COMPENSATION: "monthly-compensation/",
  GET_MONTHLY_COMPENSATION: (id) => `monthly-compensation/${id}/`,
  UPDATE_MONTHLY_COMPENSATION: (id) => `monthly-compensation/${id}/`,
  SEND_PDF_EMAIL: (id) => `monthly-compensation/${id}/send-pdf-email/`,
  DOWNLOAD_PDF: (id) => `monthly-compensation/${id}/download-pdf/`,
  BULK_SEND_PDF_EMAIL: "monthly-compensation/bulk-send-pdf-email/",
  BULK_EMAIL_STATUS: (jobId) => `monthly-compensation/bulk-send-pdf-email/${jobId}/status/`,
  BULK_APPROVE: "monthly-compensation/bulk-approve/",
};

export const clinicEndpoints = {
  // Monthly Compensation
  GET_MONTHLY_COMPENSATION_BY_CLINIC: (clinicCode) => `monthly-compensation/clinic/${clinicCode}/`,
  CREATE_MONTHLY_COMPENSATION_FOR_CLINIC: (clinicCode) =>
    `monthly-compensation/clinic/${clinicCode}/`,
  UPDATE_MONTHLY_COMPENSATION_FOR_CLINIC: (clinicCode, id) =>
    `monthly-compensation/clinic/${clinicCode}/${id}/`,
  // Reports - Read-only for Clinic (Role 3)
  GET_REPORTS_CLINICS: "reports/clinics/",

  // Clinics
  GET_CLINIC_DETAIL: (clinicCode) => `clinics/clinic-detail/${clinicCode}/`,
};

export default endpoints;
